import * as React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Translate } from '../../../../shared/LanguageService';
import { CalendarView } from '../state/calendarView';
import BrokerFilter from '../../brokerFilter/view/BrokerFilter';
import { CalendarBroker } from '../../types';

type CalendarToolbarProps = {
    title: string;
    titleId: string;
    view: CalendarView;
    onlyOwnBookings: boolean | null;
    onOnlyOwnBookingsChange: (onlyOwn: boolean) => void;
    brokers: CalendarBroker[];
    chosenBrokerId: number | null;
    onBrokerChange: (brokerId: number | null) => void;
    onPrevious: () => void;
    onNext: () => void;
    onViewChange: (view: CalendarView) => void;
};

function CalendarToolbar({
    title,
    titleId,
    view,
    onlyOwnBookings,
    onOnlyOwnBookingsChange,
    brokers,
    chosenBrokerId,
    onBrokerChange,
    onPrevious,
    onNext,
    onViewChange
}: CalendarToolbarProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                gap: 2,
                mb: 2
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <IconButton
                    onClick={onPrevious}
                    aria-label={Translate(
                        view === 'week'
                            ? 'customer-calendar/previous-week'
                            : 'customer-calendar/previous-month'
                    )}
                    size="small"
                    sx={{ border: 1, borderColor: 'divider' }}
                >
                    <ChevronLeftIcon />
                </IconButton>
                <Typography
                    variant="h5"
                    component="h1"
                    id={titleId}
                    sx={{
                        flex: { xs: 1, sm: 'none' },
                        fontWeight: 600,
                        fontSize: { xs: '1.15rem', sm: '1.5rem' },
                        textTransform: 'capitalize',
                        textAlign: 'center',
                        minWidth: { xs: 0, sm: '9em' },
                        m: 0
                    }}
                >
                    {title}
                </Typography>
                <IconButton
                    onClick={onNext}
                    aria-label={Translate(
                        view === 'week'
                            ? 'customer-calendar/next-week'
                            : 'customer-calendar/next-month'
                    )}
                    size="small"
                    sx={{ border: 1, borderColor: 'divider' }}
                >
                    <ChevronRightIcon />
                </IconButton>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    minWidth: 0
                }}
            >
                {onlyOwnBookings !== null && (
                    <BrokerFilter
                        brokers={brokers}
                        chosenId={chosenBrokerId}
                        disabled={onlyOwnBookings}
                        onChange={onBrokerChange}
                    />
                )}
                {onlyOwnBookings !== null && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={onlyOwnBookings}
                                onChange={(event) => onOnlyOwnBookingsChange(event.target.checked)}
                            />
                        }
                        label={Translate('customer-calendar/only-my-bookings')}
                        sx={{ m: 0 }}
                    />
                )}
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    fullWidth
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                    size="small"
                    aria-label={Translate('customer-calendar/view')}
                    onChange={(_event, next: CalendarView | null) => next && onViewChange(next)}
                >
                    <ToggleButton value="month" sx={{ px: 2 }}>
                        {Translate('calendars/header/button/month')}
                    </ToggleButton>
                    <ToggleButton value="week" sx={{ px: 2 }}>
                        {Translate('calendars/header/button/week')}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
        </Box>
    );
}

export default CalendarToolbar;
